import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import {
  useUploadFilesMutation,
  FileNameTypes,
  useUpdateFileStatusMutation,
  FileUploadStatus,
  GetAssemblyAttachmentsDocument,
  GetAssemblyDetailsDocument,
  ImageType,
} from '@/__generated__/graphql';
import { canvasToImage } from '@/utils/dom';
import {
  DEFAULT_SCREENSHOT_DARK_BACKGROUND,
  DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
} from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useCaptureScreenshot } from '@/hooks/useCaptureScreenshot';
import { usePoiLabelMarkerColors } from '@/hooks/usePoiLabelMarkerColors';

export const useSaveAssemblyAttachment = (
  referenceId: string,
  collectionName: string,
  setIsUpdating?: (loading: boolean) => void
) => {
  const [uploadFiles] = useUploadFilesMutation();
  const [updateFileStatus] = useUpdateFileStatusMutation();
  const structureId = useGetStructureId();
  const callbacks = useAtomValue(state.globalCallbacks);
  const { showSnackBar } = useSnackBarMessage({});

  const { captureElement } = useCaptureScreenshot();
  const { setPdfPoiLabelMarkerColors, resetPoiLabelMarkerColors } = usePoiLabelMarkerColors();

  const uploadAttachments = useCallback(
    async (attchments: Attachment[]) => {
      const { data } = await uploadFiles({
        variables: {
          input: {
            structureId,
            referenceId,
            fileNameType: FileNameTypes.UserAttachments,
            collectionName,
            files: attchments
              .filter(({ file, uuid }) => file && uuid)
              .map(({ file, name, type, uuid, imageType }) => ({
                uuid: uuid!,
                name,
                mimeType: type,
                size: file?.size!,
                imageType,
              })),
          },
        },
      });

      const presignedAttachments = data?.preSignedUrlToUploadFiles ?? [];
      if (presignedAttachments.length === 0) return [];

      for (const { url, id, uuid } of presignedAttachments) {
        const attachment = attchments.find((att) => att.uuid === uuid);
        if (!attachment || !attachment.file) continue;
        const uploadResponse = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': attachment.type },
          body: attachment.file,
        });

        if (uploadResponse.status === 200) {
          await updateFileStatus({
            variables: {
              input: {
                referenceId,
                collectionName,
                fileUpdates: [{ fileId: id, uploadStatus: FileUploadStatus.Success }],
              },
            },
            refetchQueries: [GetAssemblyAttachmentsDocument, GetAssemblyDetailsDocument],
          });
        }
      }
      return presignedAttachments;
    },
    [uploadFiles, structureId, referenceId, collectionName, updateFileStatus]
  );

  const handleCaptureScreenshot = useCallback(async () => {
    if (setIsUpdating) setIsUpdating(true);
    try {
      // Use the same approach as getViewpointScreenshot to find the element to capture
      const container = document.getElementById('outer-viewer');
      let imageBase64;
      let imageBase64ForPdf;
      const filesArray = [];

      if (!container) {
        const abyssViewerParent = document.querySelector('canvas');

        if (!abyssViewerParent) {
          const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();
          imageBase64 = canvasToImage(htmlCanvas, {
            fillStyle: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
          });
          setPdfPoiLabelMarkerColors();
          imageBase64ForPdf = canvasToImage(htmlCanvas, {
            fillStyle: DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
          });
          resetPoiLabelMarkerColors();
        } else {
          imageBase64 = await captureElement({ element: abyssViewerParent });
          setPdfPoiLabelMarkerColors();
          imageBase64ForPdf = await captureElement({
            element: abyssViewerParent,
            background: DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
          });
          resetPoiLabelMarkerColors();
        }
      } else {
        imageBase64 = await captureElement({ element: container });
        setPdfPoiLabelMarkerColors();
        imageBase64ForPdf = await captureElement({
          element: container,
          background: DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
        });
        resetPoiLabelMarkerColors();
      }

      if (!imageBase64) {
        showSnackBar('Failed to capture screenshot', 'error');
        return;
      }

      const fileName = `assembly-screenshot-${Date.now()}.png`;
      const res = await fetch(imageBase64);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: 'image/png' });
      const uuid = `file-${Date.now()}`;
      filesArray.push({
        file,
        name: fileName,
        type: 'image/png',
        caption: '',
        isNew: true,
        uuid,
      });
      if (imageBase64ForPdf) {
        const fileNameForPdf = `assembly-screenshot-pdf-${Date.now()}.png`;
        const resForPdf = await fetch(imageBase64ForPdf);
        const blobForPdf = await resForPdf.blob();
        const fileForPdf = new File([blobForPdf], fileNameForPdf, { type: 'image/png' });
        filesArray.push({
          file: fileForPdf,
          name: fileNameForPdf,
          type: 'image/png',
          caption: '',
          isNew: true,
          uuid: `${uuid}-${ImageType.WhiteBgPdf}`,
          imageType: ImageType.WhiteBgPdf,
        });
      }
      await uploadAttachments(filesArray);
    } finally {
      if (setIsUpdating) setIsUpdating(false);
      showSnackBar('Capture screenshot successfully', 'success');
    }
  }, [
    callbacks,
    captureElement,
    resetPoiLabelMarkerColors,
    setIsUpdating,
    setPdfPoiLabelMarkerColors,
    showSnackBar,
    uploadAttachments,
  ]);

  return { handleCaptureScreenshot, uploadAttachments };
};
